import { useQueryClient } from '@tanstack/react-query'
import { Button, Flex, Form, Input, Modal, Spin } from 'antd'
import type { ModalProps } from 'antd'
import { useTheme } from 'antd-style'
import { useEffect } from 'react'

import {
  useIdentityRolesServiceCreateRoleEndpoint,
  useIdentityRolesServiceGetRoleEndpoint,
  useIdentityRolesServiceGetRolesListEndpointKey,
  useIdentityRolesServiceUpdateRoleEndpoint,
  useIdentityServiceGetPermissionsListEndpoint,
  useIdentityServiceGetProfilesListEndpointKey,
  useUserServiceGetUserRolesListEndpointKey
} from 'src/api/queries'
import { ApiError, LegalEntityStatus, LegalEntityType } from 'src/api/requests'
import {
  InternalInstitutionsSelect,
  OtherInstitutionsSelect,
  requiredRules,
  SaveConfirm,
  Text
} from 'src/components'
import { useUser } from 'src/hooks'
import { useLocales } from 'src/locales'
import { formErrorHandler } from 'src/utils'

import { useStyles } from '../styles'

import { PermissionSelector } from './PermissionSelector'

type RolesFormModalProps = {
  roleId?: string
  onCancel: () => void
} & ModalProps

const getModalText = (isEdit: boolean, t: (key: string) => string) => {
  return {
    buttonText: isEdit ? t('roles.save') : t('roles.confirm'),
    title: isEdit ? t('roles.edit_role') : t('roles.add_new_role')
  }
}

export const RolesFormModal = ({ roleId, onCancel }: RolesFormModalProps) => {
  const { t } = useLocales()
  const [form] = Form.useForm()
  const { userData } = useUser()

  const { styles } = useStyles()
  const queryClient = useQueryClient()
  const palette = useTheme()

  const { buttonText, title } = getModalText(!!roleId, t)

  const { data } = useIdentityRolesServiceGetRoleEndpoint(
    {
      id: String(roleId)
    },
    [
      String(userData?.currentLegalEntityId),
      String(userData?.currentRoleId),
      roleId
    ],
    { enabled: !!roleId }
  )

  const { data: permissions, isLoading } =
    useIdentityServiceGetPermissionsListEndpoint()

  const { mutate: create, isPending: isCreating } =
    useIdentityRolesServiceCreateRoleEndpoint({
      onSuccess: () => onMutationSuccess(),
      onError: (error: ApiError) => onMutationError(error)
    })

  const { mutate: edit, isPending: isEditing } =
    useIdentityRolesServiceUpdateRoleEndpoint({
      onSuccess: () => onMutationSuccess(),
      onError: (error: ApiError) => onMutationError(error)
    })

  useEffect(() => {
    if (roleId) {
      const institutionsIds = data?.legalEntities
        ?.filter((item) => item.type === LegalEntityType.INTERNAL)
        .map((item) => ({
          value: item.id,
          label: item.name
        }))

      form.setFieldsValue({
        ...data,
        legalEntities: undefined,
        institutionsIds: institutionsIds,
        ongIds: data?.legalEntities
          ?.filter((item) => item.type === LegalEntityType.OTHER)
          .map((item) => item.id)
      })
    }
  }, [data])

  const onSubmit = async () => {
    await form.validateFields()
    const values = form.getFieldsValue(true)

    const payload = {
      ...values,
      institutionsIds: undefined,
      ongIds: undefined,
      legalEntityIds: [
        ...(values.institutionsIds?.map(
          (item: { value: string }) => item.value
        ) || []),
        ...(values.ongIds?.map((item: number) => item) || [])
      ]
    }

    roleId
      ? edit({ requestBody: payload, id: roleId })
      : create({ requestBody: payload })
  }

  const onClose = () => {
    onCancel()
    form.resetFields()
  }

  const onMutationSuccess = () => {
    onClose()
    queryClient.invalidateQueries({
      queryKey: [useIdentityRolesServiceGetRolesListEndpointKey]
    })

    queryClient.invalidateQueries({
      queryKey: [useIdentityServiceGetProfilesListEndpointKey]
    })

    queryClient.invalidateQueries({
      queryKey: [useUserServiceGetUserRolesListEndpointKey]
    })
  }

  const onMutationError = (e: ApiError) => {
    form.setFields(formErrorHandler(e.body.errors))
  }

  return (
    <Modal
      width={1000}
      maskClosable={false}
      title={
        <Flex align="center" justify="space-between">
          <Text className="modal-title">{title}</Text>

          <SaveConfirm
            title={`${buttonText}?`}
            onConfirm={onSubmit}
            placement="leftBottom"
            okButtonProps={{ loading: isCreating || isEditing }}
          >
            <Button type="primary">{buttonText}</Button>
          </SaveConfirm>
        </Flex>
      }
      closable={false}
      open
      footer={null}
      onCancel={onClose}
    >
      <Form layout="vertical" requiredMark={false} form={form}>
        <Flex vertical gap={16}>
          <Form.Item
            name="name"
            label={t('roles.title_role')}
            rules={requiredRules}
          >
            <Input placeholder={t('roles.title_role')} />
          </Form.Item>
          <Form.Item
            name="institutionsIds"
            label={t('roles.select_institutions')}
          >
            <InternalInstitutionsSelect
              apiStatus={LegalEntityStatus.ACTIVE}
              mode="multiple"
              maxTagCount={3}
              labelInValue
            />
          </Form.Item>
          <Form.Item name="ongIds" label={t('roles.select_ong')}>
            <OtherInstitutionsSelect
              apiStatus={LegalEntityStatus.ACTIVE}
              mode="multiple"
              maxTagCount={3}
            />
          </Form.Item>
          <Form.Item
            name="description"
            label={
              <div>
                {t('common.description')}{' '}
                <Text color={palette.colorTextDescription}>
                  ({t('common.optional').toLowerCase()})
                </Text>
              </div>
            }
          >
            <Input.TextArea
              className={styles.textarea}
              placeholder={t('roles.role_description')}
            />
          </Form.Item>

          <Text>{t('roles.general_permissions')}</Text>

          <Form.Item
            name="permissions"
            valuePropName="value"
            rules={requiredRules}
          >
            {isLoading ? (
              <Spin />
            ) : (
              <PermissionSelector permissions={permissions?.data} form={form} />
            )}
          </Form.Item>
        </Flex>
      </Form>
    </Modal>
  )
}
